import React from "react";
import * as strogging from "../infra/strogging";

// Explanation: In remote config, every config value is stored as a string.
// the string is a JSON string, which is parsed into a RemoteConfigValue.
// the set of all remote config values is a RemoteConfigFlags, but which has
// two extra flags, internal_user_ids and internal_team_ids, which are
// used in the determination of whether a given feature is enabled.

export interface RemoteConfigValue<T = undefined> {
  enabled_team_ids?: string[];
  enabled_user_ids?: string[];
  include_internal_team_ids?: boolean;
  include_internal_user_ids?: boolean;
  is_enabled?: boolean;
  value?: T;
}

export type RemoteConfigValueTypedFlags<T> = {
  [key in keyof T]: RemoteConfigValue<T[key]>;
};

export type RemoteConfigFlags<T> = RemoteConfigValueTypedFlags<T> & {
  internal_user_ids?: string[];
  internal_team_ids?: string[];
};

export interface FlagsContextValue<T> {
  flags: RemoteConfigFlags<T>;
  internalUserIDs: string[];
  internalTeamIDs: string[];
  refresh: () => Promise<void>;
}
export const baseValueTypes = {
  facebook_linking: undefined,
  facebook_groups: undefined,
  player_portal: undefined,
  is_shdpro: undefined,
  pro_tab: undefined,
  hitscope: undefined,
  volleyball: undefined,
  vb_sscore_v2: undefined,
  new_livestreamer_experience: undefined,
  allow_on_platform: undefined,
  monetization_tests: undefined,
  use_theme_sbot2: undefined,
  new_onboarding: undefined,
  livestreamer_setup: undefined,
  new_live_streamer_workflow: undefined,
  game_start_workflow: undefined,
  overlay_settings: undefined,
  vb_monetization: undefined,
};

export const defaultFlags: RemoteConfigValueTypedFlags<typeof baseValueTypes> =
  {
    facebook_linking: {},
    facebook_groups: {},
    player_portal: {},
    is_shdpro: { is_enabled: false },
    pro_tab: { is_enabled: false },
    hitscope: {
      is_enabled: true,
    },
    volleyball: { is_enabled: false },
    vb_sscore_v2: { is_enabled: false },
    new_livestreamer_experience: { is_enabled: false },
    allow_on_platform: { is_enabled: false },
    monetization_tests: { is_enabled: false },
    use_theme_sbot2: { is_enabled: false },
    new_onboarding: { is_enabled: false },
    livestreamer_setup: { is_enabled: false },
    new_live_streamer_workflow: { is_enabled: false },
    game_start_workflow: { is_enabled: false },
    overlay_settings: { is_enabled: false },
    vb_monetization: { is_enabled: false },
  };

export const defaultConfigFlags: { [key: string]: string } = Object.entries(
  defaultFlags
).reduce((acc, [k, v]) => ({ ...acc, [k]: JSON.stringify(v) }), {});

export type ReactFlagsContext<T> = React.Context<FlagsContextValue<T>>;

export const useFeatureEnabledValue = <T, TKey extends keyof T>(
  feature: TKey,
  flagsContext: ReactFlagsContext<T>,
  shdUserId: string | undefined,
  userEmail: string | undefined,
  teamId: string | undefined,
  teamIds: string[] = []
): { enabled: false } | { enabled: true; value: T[TKey] } => {
  const context = React.useContext(flagsContext);
  const { flags, internalTeamIDs, internalUserIDs } = context;

  const value = flags[feature];
  if (!value) {
    return { enabled: false };
  }
  if (typeof value !== "object") {
    strogging.error("useIsFeatureEnabled:value is not an object", {
      feature,
      value,
    });
    return { enabled: false };
  }

  // check the hack first
  if ("enabled_team_ids" in value && value.enabled_team_ids) {
    const validTeamIds =
      "include_internal_team_ids" in value && value.include_internal_team_ids
        ? value.enabled_team_ids.concat(internalTeamIDs)
        : value.enabled_team_ids;

    if (validTeamIds.filter((x) => teamIds.includes(x)).length > 0) {
      return { enabled: true, value: value.value as T[TKey] };
    }
  }

  if ("is_enabled" in value) {
    if (value.is_enabled) {
      return { enabled: true, value: value.value as T[TKey] };
    } else {
      return { enabled: false };
    }
  }

  if (!shdUserId) {
    return { enabled: false };
  }

  if (value.enabled_user_ids) {
    const includeInternalUserIds = value.include_internal_user_ids ?? false;
    const validUserIds = includeInternalUserIds
      ? value.enabled_user_ids.concat(internalUserIDs)
      : value.enabled_user_ids;

    if (shdUserId && validUserIds.includes(shdUserId)) {
      return { enabled: true, value: value.value as T[TKey] };
    } else if (
      includeInternalUserIds &&
      userEmail &&
      userEmail.endsWith("@diamondkinetics.com")
    ) {
      return { enabled: true, value: value.value as T[TKey] };
    }
  }

  if (value.enabled_team_ids) {
    const validTeamIds = value.include_internal_team_ids
      ? value.enabled_team_ids.concat(internalTeamIDs)
      : value.enabled_team_ids;

    if (teamId && validTeamIds.includes(teamId)) {
      return { enabled: true, value: value.value as T[TKey] };
    }
  }

  return { enabled: false };
};

export const useFeatureEnabled = <T, TKey extends keyof T>(
  feature: TKey,
  flagsContext: ReactFlagsContext<T>,
  shdUserId: string | undefined,
  userEmail: string | undefined,
  teamId: string | undefined,
  teamIds: string[] = []
): boolean => {
  return useFeatureEnabledValue(
    feature,
    flagsContext,
    shdUserId,
    userEmail,
    teamId,
    teamIds
  ).enabled;
};
